import { EventEmitter, Injectable } from '@angular/core';
import { JwtPayload } from 'jwt-decode';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { EndPoint } from '../config/endpoint';
import { School } from '../interface/school';
import { HttpService } from './http.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class SchoolService {
  isAddNewYear = new EventEmitter<any>();
  public facultyAccessCode = new EventEmitter<string>();
  private dialogSubject = new Subject<void>();
  dialogObservable$ = this.dialogSubject.asObservable();
  // BehaviorSubject to track when a new academic year is added (default: false)
  private newSchoolAdded = new BehaviorSubject<boolean>(false);

  // Observable to subscribe for new academic year updates
  newSchool$ = this.newSchoolAdded.asObservable();

  constructor(
    private httpService: HttpService,
    private storageService: StorageService,
  ) {}

  triggerDialog() {
    this.dialogSubject.next();
  }

  emitFacultyAccessCode(data: string): void {
    this.facultyAccessCode.next(data);
  }

  async schoolByCode(queryParams: object) {
    try {
      return firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.SchoolByCode}`, {
          params: queryParams,
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  async schoolById(id: number): Promise<JwtPayload> {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.School}/${id}`),
      );
      return this.httpService.decodeTokenPagination(response);
    } catch (error) {
      throw error;
    }
  }

  async teacherBellSheduleByUserId(queryParams) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_bell_shedules}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async addTeacherBellShedule(body) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_bell_shedules}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async updateTeacherBellShedule(id, body) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_bell_shedules}/${id}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async getAllSchools(queryParams?) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.School}`, {
          params: queryParams,
        }),
      );
      return queryParams && queryParams.page
        ? this.httpService.decodeTokenPagination(response)
        : this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getMultipleSchoolsByIds(body) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_multiple_schools_by_ids}`, body),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async addSchool(body) {
    try {
      return firstValueFrom(this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.School}`, body));
    } catch (error) {
      throw error;
    }
  }

  async updateSchool(id, body) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.School}/${id}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getSchoolBellSchedules(queryParams?) {
    try {
      const token = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.school_bell_shedules}`, {
          params: queryParams,
        }),
      );

      return this.httpService.decodeToken(token);
    } catch (error) {
      throw error;
    }
  }

  async getSchoolBellSchedulesById(id: number) {
    try {
      const token = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.school_bell_shedules}/${id}`),
      );
      return token;
      // return this.httpService.decodeToken(token);
    } catch (error) {
      throw error;
    }
  }

  async addSchoolBellShedule(body) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.school_bell_shedules}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async updateSchoolBellShedule(id, body) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.school_bell_shedules}/${id}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async deleteBellSchedule(id: number) {
    try {
      return firstValueFrom(
        this.httpService.delete<any>(`${environment.XANO_SERVER_URL}${EndPoint.school_bell_shedules}/${id}`),
      );
    } catch (error) {
      throw error;
    }
  }

  async addSchoolLogo(body) {
    try {
      return firstValueFrom(this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.school_logo}`, body));
    } catch (error) {
      throw error;
    }
  }

  async getSchoolLogoById(id: number) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.school_logo}/${id}`),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async addTransferData(body) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.students_school_transfer}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getBulkUserData(queryParams: object) {
    try {
      return firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.bulk_upload_process}`, {
          params: queryParams,
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  async getSchoolTransferUsingUserId(queryParams) {
    try {
      return firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.students_school_transfer_using_user_id}`, {
          params: queryParams,
        }),
      );
    } catch (error) {
      throw error;
    }
  }
  async getSchoolLogoBySchoolID(id) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.school_logo}/${id}`),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async addMultipleSchoolBellShedule(body) {
    try {
      const schoolToken = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.add_multiple_bell_schedule}`, body),
      );

      return this.httpService.decodeToken(schoolToken);
    } catch (error) {
      throw error;
    }
  }

  async getMultipleBellSchedulesByScheduleId(body) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_multiple_bell_schedules_by_id}`, body),
      );
      return response;
      // console.log(this.httpService.decodeToken(response));
      // return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async updateFacultyStatucCodeSchool(body) {
    try {
      const response = await firstValueFrom(
        this.httpService.put<any>(`${environment.XANO_SERVER_URL}${EndPoint.school_update_faculty_status_code}`, body),
      );

      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getTeacherBySchoolId(queryParams: any) {
    try {
      const teachers = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_all_teachers_list}`, {
          params: queryParams,
        }),
      );
      return teachers;
    } catch (error) {
      throw error;
    }
  }

  async getSchoolBellShedules(queryParams?) {
    try {
      const token = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.school_bell_shedules}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeToken(token);
    } catch (error) {
      // console.log(error);
    }
  }

  async checkIfCDSNumberAlreadyExist(queryParams) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.check_if_cds_number_exist}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async updateCDSNumber(cdsNumber: string, school: School) {
    try {
      if (cdsNumber !== '') {
        const schoolData = this.storageService.getItem('selectedSchoolByAdmin');
        school['CDS_number'] = cdsNumber;
        const response = await this.updateSchool(school.id, school);
        if (response && response['id']) {
          if (schoolData) {
            this.storageService.removeItem('selectedSchoolByAdmin');
            this.storageService.setItem('selectedSchoolByAdmin', response);
          }
        }
      } else {
        return;
      }
    } catch (error) {
      console.log('SchoolService ~ updateCDSNumber ~ error:', error);
    }
  }

  async checkIfCDSNumberExists(cdsNumber: string): Promise<boolean> {
    try {
      if (cdsNumber === '') {
        return true;
      } else {
        const checkIfNumberExists = await this.checkIfCDSNumberAlreadyExist({
          cds_number: cdsNumber,
        });
        if (checkIfNumberExists.length > 0) {
          return false;
        } else {
          return true;
        }
      }
    } catch (error) {
      console.log('SchoolService ~ checkIfCDSNumberExists ~ error:', error);
    }
  }

  /**
   * Fetch users by school ID.
   * This method retrieves all user roles (students, teachers, and admins)
   * for a specific school using its school ID. .
   * @param queryParams - The query parameters containing the school ID.
   * @returns A promise resolving to the list of users (students, teachers, admins).
   */
  async getUserBySchoolId(queryParams): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_users_by_school_id}`, {
          params: queryParams,
        }),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getTeacherBellSchedulesById(id: number) {
    try {
      const token = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_bell_shedules}/${id}`),
      );
      return token;
      // return this.httpService.decodeToken(token);
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Emit an update when a new school is added
   * @param isNewSchoolAdded - flag indicating if a new school is added
   */
  emitNewSchoolAdded(isNewSchoolAdded: boolean) {
    this.newSchoolAdded.next(isNewSchoolAdded);
  }
}
